import {
  VisualizationDefinition,
  VisualizationDefinitionFilter,
  VisualizationFilterMetadata,
  VisualizerFind,
} from './types';

const FILTER_KEYS = ['eras', 'findTypes', 'materials', 'tags'] as const;

function normalizeFilterValue(value: string): string {
  return value.trim().toLowerCase();
}

function includesNormalized(values: string[], candidate: string): boolean {
  const normalizedCandidate = normalizeFilterValue(candidate);

  return values.some(
    (value) => normalizeFilterValue(value) === normalizedCandidate
  );
}

function expandEras(
  values: string[] | undefined,
  metadata: VisualizationFilterMetadata
): string[] {
  if (!values?.length) {
    return [];
  }

  const expanded = new Set(values.map(normalizeFilterValue));
  let addedDescendant = true;

  while (addedDescendant) {
    addedDescendant = false;

    metadata.eras.forEach((era) => {
      if (
        era.parentId &&
        expanded.has(normalizeFilterValue(era.parentId)) &&
        !expanded.has(normalizeFilterValue(era.id))
      ) {
        expanded.add(normalizeFilterValue(era.id));
        addedDescendant = true;
      }
    });
  }

  return [...expanded];
}

function expandFindTypes(
  values: string[] | undefined,
  metadata: VisualizationFilterMetadata
): string[] {
  if (!values?.length) {
    return [];
  }

  return metadata.findTypes
    .filter(
      (findType) =>
        includesNormalized(values, findType.id) ||
        (findType.category && includesNormalized(values, findType.category))
    )
    .map((findType) => normalizeFilterValue(findType.id));
}

function hasAny(values: string[] | undefined, candidates: string[]): boolean {
  return !!values?.some((value) => includesNormalized(candidates, value));
}

export function matchesVisualizationDefinition(
  find: VisualizerFind,
  filter: VisualizationDefinitionFilter,
  metadata: VisualizationFilterMetadata
): boolean {
  const eras = expandEras(filter.eras, metadata);
  const findTypes = expandFindTypes(filter.findTypes, metadata);

  if (
    filter.eras?.length &&
    (!find.era || !eras.includes(normalizeFilterValue(find.era)))
  ) {
    return false;
  }

  if (
    filter.findTypes?.length &&
    (!find.type || !findTypes.includes(normalizeFilterValue(find.type)))
  ) {
    return false;
  }

  if (
    filter.materials?.length &&
    (!find.material || !includesNormalized(filter.materials, find.material))
  ) {
    return false;
  }

  if (filter.tags?.length && !hasAny(find.tags || undefined, filter.tags)) {
    return false;
  }

  return FILTER_KEYS.some((key) => !!filter[key]?.length);
}

function effectiveValueCount(
  filter: VisualizationDefinitionFilter,
  metadata: VisualizationFilterMetadata
): number {
  return (
    expandEras(filter.eras, metadata).length +
    expandFindTypes(filter.findTypes, metadata).length +
    (filter.materials?.length || 0) +
    (filter.tags?.length || 0)
  );
}

function specificity(
  definition: VisualizationDefinition,
  index: number,
  metadata: VisualizationFilterMetadata
): number[] {
  const { filter } = definition;
  const dimensionCount = FILTER_KEYS.filter(
    (key) => !!filter[key]?.length
  ).length;

  return [
    dimensionCount,
    filter.tags?.length ? 1 : 0,
    filter.findTypes?.length ? 1 : 0,
    filter.materials?.length ? 1 : 0,
    filter.eras?.length ? 1 : 0,
    -effectiveValueCount(filter, metadata),
    index,
  ];
}

function compareScores(first: number[], second: number[]): number {
  for (let index = 0; index < first.length; index += 1) {
    if (first[index] !== second[index]) {
      return first[index] - second[index];
    }
  }

  return 0;
}

export function getMostSpecificDefinition(
  find: VisualizerFind,
  definitions: VisualizationDefinition[],
  metadata: VisualizationFilterMetadata
): VisualizationDefinition | null {
  let match: VisualizationDefinition | null = null;
  let matchScore: number[] | null = null;

  definitions.forEach((definition, index) => {
    if (!matchesVisualizationDefinition(find, definition.filter, metadata)) {
      return;
    }

    const score = specificity(definition, index, metadata);
    if (!matchScore || compareScores(score, matchScore) > 0) {
      match = definition;
      matchScore = score;
    }
  });

  return match;
}
